import React, { useCallback, useRef, useState, type SyntheticEvent } from 'react';
import type { UIAnalyticsEvent } from '@atlaskit/analytics-next';
import { CustomThemeButton } from '@atlaskit/button';
import DropdownMenu, { type CustomTriggerProps, DropdownItem } from '@atlaskit/dropdown-menu';
import MoreIcon from '@atlaskit/icon/glyph/more';
import { token } from '@atlaskit/tokens';
import { ComponentWithAnalytics } from '@atlassian/jira-analytics-web-react';
import { useIntl } from '@atlassian/jira-intl';
import { fireUIAnalytics } from '@atlassian/jira-product-analytics-bridge';
import { SprintActionsConnectMenuItems } from '@atlassian/jira-software-connect-toolbar';
import { editSprintModalLoad } from '@atlassian/jira-software-edit-sprint-modal-relay/async';
import { useModalDialogActions } from '@atlassian/jira-software-modal-dialog';
import {
	SPRINT,
	SPRINT_MOVE_TO_TOP,
	SPRINT_MOVE_UP,
	SPRINT_MOVE_DOWN,
	SPRINT_MOVE_TO_BOTTOM,
} from '../../../model/constants';
import { DeleteSprintType } from '../../../model/modals/types';
import type { CardListType, CardListId } from '../../../model/types';
import { useBacklogDispatch, useBacklogSelector } from '../../../state';
import { openModal } from '../../../state/actions/modals';
import { sprintMoveRequest } from '../../../state/actions/sprints/move';
import {
	makeIsCardListActiveSprint,
	makeGetNextSprintId,
	makeGetPreviousNotStartedSprintId,
	getFirstInactiveSprintId,
	getLastInactiveSprintId,
} from '../../../state/selectors/sprints';
import { useIsCMPBacklog } from '../../../state/state-hooks/capabilities';
import EditSprintModalRelay from '../../modals/edit-sprint-modal-relay';
import { darkerButtonTheme } from '../utils';
import messages from './messages';

const DropdownItemWithAnalytics = ComponentWithAnalytics('dropdownItem', {
	onClick: 'clicked',
})(DropdownItem);

export default function Menu({
	cardListId,
	cardListName,
	cardListType,
}: {
	cardListId: CardListId;
	cardListName: string;
	cardListType: CardListType;
}) {
	const isCMPBoard = useIsCMPBacklog();
	const { formatMessage } = useIntl();
	const { setReturnFocusTo } = useModalDialogActions();
	const element = useRef<HTMLElement | null>(null);
	const [isEditSprintModalOpen, setIsEditSprintModalOpen] = useState(false);
	const previousSprintId = useBacklogSelector((state) =>
		makeGetPreviousNotStartedSprintId(state)(cardListId),
	);
	const nextSprintId = useBacklogSelector((state) => makeGetNextSprintId(state)(cardListId));

	const firstInactiveSprintId = useBacklogSelector(getFirstInactiveSprintId);
	const lastInactiveSprintId = useBacklogSelector(getLastInactiveSprintId);

	const isCardListActiveSprint = useBacklogSelector((state) =>
		makeIsCardListActiveSprint(state)(cardListId),
	);

	const canMoveSprintUp = previousSprintId !== null;
	const canMoveSprintDown = !isCardListActiveSprint && nextSprintId !== null;

	const enabledMenuActions: { [key: string]: boolean } =
		cardListType !== SPRINT
			? {}
			: {
					MOVE_SPRINT_TO_TOP: canMoveSprintUp,
					MOVE_SPRINT_UP: canMoveSprintUp,
					MOVE_SPRINT_DOWN: canMoveSprintDown,
					MOVE_SPRINT_TO_BOTTOM: canMoveSprintDown,
					EDIT_SPRINT: true,
					DELETE_SPRINT: true,
				};

	const dispatch = useBacklogDispatch();

	const renderTrigger = useCallback(
		({ triggerRef, ...props }: CustomTriggerProps) => {
			const handleRef = (refElement: HTMLElement | null) => {
				if (typeof triggerRef === 'function') {
					triggerRef(refElement);
				}
				if (refElement) {
					element.current = refElement;
				}
			};
			const moreIcon = (
				<MoreIcon label="More actions" primaryColor={token('color.icon', '#44546F')} />
			);
			return (
				<CustomThemeButton
					{...props}
					ref={handleRef}
					iconBefore={moreIcon}
					aria-label={formatMessage(messages.menuTrigger, {
						sprintName: cardListName,
					})}
					theme={darkerButtonTheme}
					testId={`software-backlog.card-list.sprints-menu.${cardListName}`}
				/>
			);
		},
		[cardListName, formatMessage],
	);

	const handleOpenChange = useCallback(
		() => setReturnFocusTo(element),
		[element, setReturnFocusTo],
	);

	const isVisible = enabledMenuActions && Object.keys(enabledMenuActions).length > 0;
	if (!isVisible) {
		return null;
	}

	const onDelete = () => {
		dispatch(
			openModal({
				type: DeleteSprintType,
				payload: { sprintId: cardListId },
			}),
		);
	};

	const onMoveUp = (event: SyntheticEvent, analyticsEvent: UIAnalyticsEvent) => {
		if (previousSprintId != null && previousSprintId !== '') {
			dispatch(
				sprintMoveRequest({
					sprintId: cardListId,
					targetSprintId: previousSprintId,
					direction: SPRINT_MOVE_UP,
					analyticsEvent,
				}),
			);
		}

		fireUIAnalytics(analyticsEvent, 'moveSprintUp');
	};

	const onMoveToTop = (event: SyntheticEvent, analyticsEvent: UIAnalyticsEvent) => {
		dispatch(
			sprintMoveRequest({
				sprintId: cardListId,
				targetSprintId: firstInactiveSprintId,
				direction: SPRINT_MOVE_TO_TOP,
				analyticsEvent,
			}),
		);

		fireUIAnalytics(analyticsEvent, 'moveSprintToTop');
	};

	const onMoveDown = (event: SyntheticEvent, analyticsEvent: UIAnalyticsEvent) => {
		if (nextSprintId != null && nextSprintId !== '') {
			dispatch(
				sprintMoveRequest({
					sprintId: cardListId,
					targetSprintId: nextSprintId,
					direction: SPRINT_MOVE_DOWN,
					analyticsEvent,
				}),
			);
		}

		fireUIAnalytics(analyticsEvent, 'moveSprintDown');
	};

	const onMoveToBottom = (event: SyntheticEvent, analyticsEvent: UIAnalyticsEvent) => {
		dispatch(
			sprintMoveRequest({
				sprintId: cardListId,
				targetSprintId: lastInactiveSprintId,
				direction: SPRINT_MOVE_TO_BOTTOM,
				analyticsEvent,
			}),
		);

		fireUIAnalytics(analyticsEvent, 'moveSprintToBottom');
	};

	const onEditSprintClick = (event: SyntheticEvent, analyticsEvent: UIAnalyticsEvent) => {
		setIsEditSprintModalOpen(true);
		fireUIAnalytics(analyticsEvent, 'openEditSprint');
		editSprintModalLoad.start();
	};

	const onEditSprintModalCloseClick = () => {
		setIsEditSprintModalOpen(false);
	};

	const menuItems = (
		<>
			{enabledMenuActions.MOVE_SPRINT_TO_TOP && (
				<DropdownItemWithAnalytics
					data-testid="software-backlog.card-list.sprints-menu.sprint-move-to-top"
					onClick={onMoveToTop}
				>
					{formatMessage(messages.moveSprintToTop)}
				</DropdownItemWithAnalytics>
			)}
			{enabledMenuActions.MOVE_SPRINT_UP && (
				<DropdownItemWithAnalytics
					data-testid="software-backlog.card-list.sprints-menu.sprint-move-up"
					onClick={onMoveUp}
				>
					{formatMessage(messages.moveSprintUp)}
				</DropdownItemWithAnalytics>
			)}
			{enabledMenuActions.MOVE_SPRINT_DOWN && (
				<DropdownItemWithAnalytics
					data-testid="software-backlog.card-list.sprints-menu.sprint-move-down"
					onClick={onMoveDown}
				>
					{formatMessage(messages.moveSprintDown)}
				</DropdownItemWithAnalytics>
			)}
			{enabledMenuActions.MOVE_SPRINT_TO_BOTTOM && (
				<DropdownItemWithAnalytics
					data-testid="software-backlog.card-list.sprints-menu.sprint-move-to-bottom"
					onClick={onMoveToBottom}
				>
					{formatMessage(messages.moveSprintToBottom)}
				</DropdownItemWithAnalytics>
			)}
			{enabledMenuActions.EDIT_SPRINT && (
				<DropdownItemWithAnalytics
					data-testid="software-backlog.card-list.sprints-menu.sprint-edit"
					onClick={onEditSprintClick}
				>
					{formatMessage(messages.editSprint)}
				</DropdownItemWithAnalytics>
			)}
			{enabledMenuActions.DELETE_SPRINT && (
				<DropdownItemWithAnalytics
					data-testid="software-backlog.card-list.sprints-menu.sprint-delete"
					onClick={onDelete}
				>
					{formatMessage(messages.deleteSprint)}
				</DropdownItemWithAnalytics>
			)}
			{isCMPBoard && (
				<>
					<SprintActionsConnectMenuItems sprintId={cardListId} location="sprint-move-actions" />
					<SprintActionsConnectMenuItems sprintId={cardListId} location="sprint-edit-action" />
					<SprintActionsConnectMenuItems sprintId={cardListId} location="sprint-delete-action" />
				</>
			)}
		</>
	);

	return (
		<>
			{isEditSprintModalOpen && (
				<EditSprintModalRelay
					cardListId={Number(cardListId)}
					onClose={onEditSprintModalCloseClick}
					onSubmit={onEditSprintModalCloseClick}
				/>
			)}
			<DropdownMenu
				onOpenChange={handleOpenChange}
				trigger={renderTrigger}
				placement="bottom-end"
				shouldRenderToParent
				testId="software-backlog.card-list.sprints-menu.dropdown-menu"
			>
				{menuItems}
			</DropdownMenu>
		</>
	);
}
